import { useEffect } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import api from '../api/client.js';
import ExportButton from '../components/ExportButton.jsx';

const PAGE_SIZE = 50;
const FILTER_KEYS = ['q', 'branch_id', 'status', 'city', 'sort', 'trend'];
const FILTERS_STORAGE_KEY = 'utsales:customers:filters';

const SORTS = [
  { value: 'name',      label: 'Name (A–Z)' },
  { value: 'stale',     label: 'Most overdue (no touch)' },
  { value: 'recent',    label: 'Recently touched' },
  { value: 'revenue',   label: 'Revenue (T12 high→low)' },
  { value: 'declining', label: 'Declining (steepest drop)' },
];

const TRENDS = [
  { value: '',       label: 'Any trend' },
  { value: 'under',  label: 'Behind' },
  { value: 'silent', label: 'Silent (no T12)' },
  { value: 'over',   label: 'Ahead' },
  { value: 'flat',   label: 'Flat' },
  { value: 'new',    label: 'New' },
];

export default function Customers() {
  // Filters live in the URL (not local state) so they survive navigating away
  // and back — e.g. clicking into a customer and hitting Back — instead of
  // resetting every time this page remounts (Ryan/Marissa, 2026-07-18).
  const [searchParams, setSearchParams] = useSearchParams();

  // The URL alone doesn't cover every path back to this page — the nav bar's
  // "Customers" link is a bare `/customers` with no query string, so it always
  // lands here with an empty URL and the filters above were lost. Mirror the
  // filter set into sessionStorage on every change, and on a genuinely bare
  // arrival (no query string at all — i.e. not a Back/Forward that already has
  // a URL to restore), rehydrate the URL from the last saved filters instead of
  // defaulting to "no filters" (operator, 2026-07-23: keep the filter until we
  // explicitly change it, regardless of where we navigate to).
  useEffect(() => {
    if (searchParams.toString() !== '') return;
    let saved;
    try { saved = JSON.parse(sessionStorage.getItem(FILTERS_STORAGE_KEY)); } catch { saved = null; }
    if (!saved) return;
    const next = new URLSearchParams();
    for (const key of FILTER_KEYS) {
      if (saved[key]) next.set(key, saved[key]);
    }
    if (next.toString()) setSearchParams(next, { replace: true });
    // Runs once on mount only — this is specifically for the "just arrived
    // with a bare URL" case, not something to re-run on every filter change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const q = searchParams.get('q') || '';
  const branchId = searchParams.get('branch_id') || '';
  const status = searchParams.get('status') || '';
  const city = searchParams.get('city') || '';
  const sort = searchParams.get('sort') || 'name';
  const trend = searchParams.get('trend') || '';
  const offset = parseInt(searchParams.get('offset'), 10) || 0;

  // Keep sessionStorage in sync with the current filters (including an
  // explicit clear-to-blank) so the next bare arrival restores exactly this.
  useEffect(() => {
    const obj = {};
    if (q) obj.q = q;
    if (branchId) obj.branch_id = branchId;
    if (status) obj.status = status;
    if (city) obj.city = city;
    if (sort) obj.sort = sort;
    if (trend) obj.trend = trend;
    if (Object.keys(obj).length) sessionStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(obj));
    else sessionStorage.removeItem(FILTERS_STORAGE_KEY);
  }, [q, branchId, status, city, sort, trend]);

  // Any filter change resets paging to page 1. `replace: true` so typing in
  // the search box doesn't spam browser history with one entry per keystroke.
  const onFilter = (key) => (v) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (v) next.set(key, v); else next.delete(key);
      next.delete('offset');
      return next;
    }, { replace: true });
  };
  const setOffset = (newOffset) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (newOffset) next.set('offset', String(newOffset)); else next.delete('offset');
      return next;
    }, { replace: true });
  };

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => api.get('/branches').then(r => r.data.data),
    staleTime: 5 * 60_000,
  });

  const params = {
    limit: PAGE_SIZE,
    offset,
    sort,
    ...(q ? { q } : {}),
    ...(branchId ? { branch_id: branchId } : {}),
    ...(status ? { status } : {}),
    ...(city ? { city } : {}),
    ...(trend ? { trend } : {}),
  };

  const list = useQuery({
    queryKey: ['customers', params],
    queryFn: () => api.get('/customers', { params }).then(r => r.data),
    placeholderData: keepPreviousData,
  });

  // Export mirrors the current filters/sort — no pagination, so limit/offset
  // are deliberately left out.
  const exportQs = new URLSearchParams({
    sort,
    ...(q ? { q } : {}),
    ...(branchId ? { branch_id: branchId } : {}),
    ...(status ? { status } : {}),
    ...(city ? { city } : {}),
    ...(trend ? { trend } : {}),
  }).toString();

  const rows = list.data?.data || [];
  const total = list.data?.meta?.total || 0;
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE_SIZE, total);

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="section-eyebrow">Accounts</div>
          <h1 className="text-2xl">Customers</h1>
          <div className="rule-crimson" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <ExportButton path={`/export/customers.csv?${exportQs}`} />
          <Link to="/customers/due-for-visit" className="btn-ghost">📍 Plan visits</Link>
          <Link to="/customers/schedule" className="btn-ghost">🗓️ Schedule</Link>
        </div>
      </header>

      {/* Filters */}
      <div className="card p-3 grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
        <div className="col-span-2 sm:col-span-2">
          <label className="label">Search</label>
          <input className="input" placeholder="Name or account #…"
                 value={q} onChange={e => onFilter('q')(e.target.value)} />
        </div>
        <div>
          <label className="label">Branch</label>
          <select className="input" value={branchId} onChange={e => onFilter('branch_id')(e.target.value)}>
            <option value="">All</option>
            {branches.data?.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Status</label>
          <select className="input" value={status} onChange={e => onFilter('status')(e.target.value)}>
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="prospect">Prospect</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div>
          <label className="label">Trend</label>
          <select className="input" value={trend} onChange={e => onFilter('trend')(e.target.value)}>
            {TRENDS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Sort</label>
          <select className="input" value={sort} onChange={e => onFilter('sort')(e.target.value)}>
            {SORTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div className="col-span-2 sm:col-span-1">
          <label className="label">City contains</label>
          <input className="input" placeholder="Fresno…"
                 value={city} onChange={e => onFilter('city')(e.target.value)} />
        </div>
      </div>

      {/* Result count + pager */}
      <div className="flex items-center justify-between text-sm text-muted">
        <span>{total === 0 ? 'No matching customers' : `Showing ${from}–${to} of ${total}`}</span>
        <div className="flex gap-2">
          <button className="btn-ghost text-sm" disabled={offset === 0 || list.isFetching}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>← Prev</button>
          <button className="btn-ghost text-sm" disabled={to >= total || list.isFetching}
                  onClick={() => setOffset(offset + PAGE_SIZE)}>Next →</button>
        </div>
      </div>

      {/* List */}
      <div className="space-y-2">
        {list.isLoading && <div className="card p-4 text-muted text-sm">Loading…</div>}
        {list.isError && <div className="card p-4 text-crimson text-sm">Failed to load customers.</div>}
        {!list.isLoading && rows.length === 0 && (
          <div className="card p-4 text-muted text-sm">No customers match these filters.</div>
        )}
        {rows.map(c => <CustomerRow key={c.id} c={c} />)}
      </div>
    </div>
  );
}

function CustomerRow({ c }) {
  const touchClass = c.days_since_touch > 90 ? 'text-crimson font-semibold'
    : c.days_since_touch > 30 ? 'text-navy' : 'text-muted';
  return (
    <Link to={`/customers/${c.id}`} className="card p-3 block hover:ring-1 hover:ring-crimson">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium text-navy truncate">{c.name}</div>
          <div className="text-xs text-muted truncate">
            {[c.city, c.state].filter(Boolean).join(', ') || '—'}
            {c.account_number && <> · #{c.account_number}</>}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 text-right">
          <TrendChip trend={c.trend} pct={c.trend_pct} t12={c.rev_t12} />
          <div className="text-xs">
            <div className={touchClass}>{c.days_since_touch}d touch</div>
            <div className="text-muted">
              {c.days_since_visit == null ? 'no visit' : `${c.days_since_visit}d visit`}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

// Matches the scorecard's verdict vocabulary (over=AHEAD, under=BEHIND, …).
const TREND_MAP = {
  over:   { label: 'AHEAD',  color: 'text-emerald-700' },
  under:  { label: 'BEHIND', color: 'text-crimson' },
  flat:   { label: 'FLAT',   color: 'text-navy' },
  new:    { label: 'NEW',    color: 'text-emerald-700' },
  silent: { label: 'SILENT', color: 'text-crimson' },
};

function TrendChip({ trend, pct, t12 }) {
  const v = TREND_MAP[trend] || TREND_MAP.flat;
  return (
    <div className="text-right w-24">
      <div className={clsx('text-xs font-bold', v.color)}>
        {v.label}
        {pct != null && (trend === 'over' || trend === 'under') && (
          <span> {pct > 0 ? '+' : ''}{pct.toFixed(0)}%</span>
        )}
      </div>
      <div className="text-xs text-muted">{fmtMoney(t12)} T12</div>
    </div>
  );
}

function fmtMoney(n) {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1000) return `$${Math.round(v / 1000)}k`;
  return `$${Math.round(v)}`;
}
